import { computed } from '@nuxtjs/composition-api';
import { sharedRef, useVSFContext } from '@vue-storefront/core';
import { useApiError } from '../useApiError';

export const useSubscribe = (): any => {
  // Loads context used to call API endpoint
  const context = useVSFContext();
  const { set: setError } = useApiError();

  // Shared ref holding the response from the API
  const subscribe = sharedRef(null, 'useSubscribe');

  // Shared ref indicating whether any method is waiting for the data from the API
  const loading = sharedRef(false, 'useSubscribe-loading');

  // Shared ref holding errors from the methods
  const error = sharedRef(null, 'useSubscribe-error');

  // Method to call an API endpoint and update `result`, `loading` and `error` properties
  const load = async () => {
    try {
      loading.value = true;
      error.value = null;
      // Change "yourIntegration" to the name of the integration
      const res = await context.$xbuy.api.subscribeList();
      if (res && res.code > 0) {
        setError(res);
        error.value = new Error(res.message);
      } else {
        subscribe.value = res.data;
      }
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  // Method to call an API endpoint and update `result`, `loading` and `error` properties
  const sub = async (params: any) => {
    try {
      loading.value = true;
      error.value = null;
      // Change "yourIntegration" to the name of the integration
      const res = await context.$xbuy.api.subscribeAdd(params);
      setError(res);
      if (res && res.code > 0) {
        error.value = new Error(res.message);
      } else {
        await load();
      }
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  // Method to call an API endpoint and update `result`, `loading` and `error` properties
  const unsub = async (params: any) => {
    try {
      loading.value = true;
      error.value = null;

      // Change "yourIntegration" to the name of the integration
      const res = await context.$xbuy.api.subscribeDelete(params);
      setError(res);
      if (res && res.code > 0) {
        error.value = new Error(res.message);
      } else {
        subscribe.value = null;
      }
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  return {
    load,
    sub,
    unsub,
    info: computed(() => subscribe.value),
    isSubed: computed(() => Boolean(subscribe.value && subscribe.value.id)),
    loading: computed(() => loading.value),
    error: computed(() => error.value)
  };
};
